import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerIcon from '@mui/icons-material/Timer';
import BuildIcon from '@mui/icons-material/Build';
import ScienceIcon from '@mui/icons-material/Science';
import YardIcon from '@mui/icons-material/Yard';
import EditIcon from '@mui/icons-material/Edit';
import { useGrow } from '../context/GrowContext';

// Icon mapping for reminders
const REMINDER_ICONS = {
  timer: { icon: TimerIcon, color: '#6366f1', label: 'Timer' },
  water: { icon: WaterDropIcon, color: '#3b82f6', label: 'Water' },
  clean: { icon: CleaningServicesIcon, color: '#22c55e', label: 'Clean' },
  feed: { icon: RestaurantIcon, color: '#f59e0b', label: 'Feed' },
  check: { icon: CheckCircleIcon, color: '#10b981', label: 'Check' },
  maintain: { icon: BuildIcon, color: '#8b5cf6', label: 'Maintain' },
  science: { icon: ScienceIcon, color: '#ec4899', label: 'Science' },
  plant: { icon: YardIcon, color: '#84cc16', label: 'Plant' },
};

// Calculate reminder progress (0-1)
function getReminderProgress(lastReset, intervalHours) {
  const now = new Date();
  const lastResetDate = new Date(lastReset);
  const elapsedMs = now - lastResetDate;
  const intervalMs = intervalHours * 60 * 60 * 1000;
  return Math.min(elapsedMs / intervalMs, 1);
}

// Format time remaining
function formatTimeRemaining(lastReset, intervalHours) {
  const now = new Date();
  const lastResetDate = new Date(lastReset);
  const elapsedMs = now - lastResetDate;
  const intervalMs = intervalHours * 60 * 60 * 1000;
  const remainingMs = Math.max(0, intervalMs - elapsedMs);

  if (remainingMs === 0) return 'Due now';

  const hours = Math.floor(remainingMs / (1000 * 60 * 60));
  const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return `${days}d ${remHours}h`;
  }
  return `${hours}h ${minutes}m`;
}

// Add/Edit Reminder Dialog
function ReminderDialog({ open, onClose, onSave, onDelete, editingReminder }) {
  const [label, setLabel] = React.useState('');
  const [icon, setIcon] = React.useState('timer');
  const [intervalValue, setIntervalValue] = React.useState(24);
  const [intervalUnit, setIntervalUnit] = React.useState('hours');

  const isEditing = !!editingReminder;

  React.useEffect(() => {
    if (open) {
      if (editingReminder) {
        setLabel(editingReminder.label || '');
        setIcon(editingReminder.icon || 'timer');
        const hours = editingReminder.intervalHours || 24;
        if (hours >= 24 && hours % 24 === 0) {
          setIntervalValue(hours / 24);
          setIntervalUnit('days');
        } else {
          setIntervalValue(hours);
          setIntervalUnit('hours');
        }
      } else {
        setLabel('');
        setIcon('timer');
        setIntervalValue(24);
        setIntervalUnit('hours');
      }
    }
  }, [open, editingReminder]);

  const handleSave = () => {
    const intervalHours = intervalUnit === 'days' ? intervalValue * 24 : intervalValue;
    onSave({
      label: label.trim() || 'Reminder',
      icon,
      intervalHours,
    });
    onClose();
  };

  const handleDelete = () => {
    if (editingReminder && onDelete) {
      onDelete(editingReminder.id);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isEditing ? <EditIcon sx={{ color: 'white', fontSize: 20 }} /> : <AccessTimeIcon sx={{ color: 'white', fontSize: 20 }} />}
          </Box>
          <Typography variant="h6" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '1rem' }}>
            {isEditing ? 'Edit Reminder' : 'Add Reminder'}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Label */}
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', mb: 0.5, display: 'block' }}>
              Label
            </Typography>
            <TextField
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Clean reservoir"
              size="small"
              fullWidth
              autoFocus
            />
          </Box>

          {/* Icon selection */}
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', mb: 1, display: 'block' }}>
              Icon
            </Typography>
            <ToggleButtonGroup
              value={icon}
              exclusive
              onChange={(_, v) => v && setIcon(v)}
              size="small"
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
            >
              {Object.entries(REMINDER_ICONS).map(([key, { icon: Icon, color }]) => (
                <ToggleButton
                  key={key}
                  value={key}
                  sx={{
                    width: 44,
                    height: 44,
                    border: '1px solid',
                    borderColor: icon === key ? color : 'divider',
                    bgcolor: icon === key ? `${color}20` : 'transparent',
                    '&:hover': {
                      bgcolor: `${color}10`,
                    },
                    '&.Mui-selected': {
                      bgcolor: `${color}20`,
                      borderColor: color,
                      '&:hover': {
                        bgcolor: `${color}30`,
                      },
                    },
                  }}
                >
                  <Icon sx={{ color: icon === key ? color : 'text.secondary', fontSize: 22 }} />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Interval */}
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', mb: 0.5, display: 'block' }}>
              Repeat Every
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                type="number"
                value={intervalValue}
                onChange={(e) => setIntervalValue(Math.max(1, parseInt(e.target.value) || 1))}
                size="small"
                sx={{ width: 100 }}
                inputProps={{ min: 1 }}
              />
              <Select
                value={intervalUnit}
                onChange={(e) => setIntervalUnit(e.target.value)}
                size="small"
                sx={{ minWidth: 100 }}
              >
                <MenuItem value="hours">Hours</MenuItem>
                <MenuItem value="days">Days</MenuItem>
              </Select>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: isEditing ? 'space-between' : 'flex-end' }}>
        {isEditing && (
          <Button
            onClick={handleDelete}
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            sx={{
              fontFamily: 'Inter, sans-serif',
              textTransform: 'none',
            }}
          >
            Delete
          </Button>
        )}
        <Stack direction="row" spacing={1}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              fontFamily: 'Inter, sans-serif',
              textTransform: 'none',
              px: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              fontFamily: 'Inter, sans-serif',
              textTransform: 'none',
              px: 2,
            }}
          >
            {isEditing ? 'Save' : 'Add'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

// Single Reminder Meter component
function ReminderMeter({ reminder, onReset, onEdit }) {
  const progress = getReminderProgress(reminder.lastReset, reminder.intervalHours);
  const timeRemaining = formatTimeRemaining(reminder.lastReset, reminder.intervalHours);
  const isDue = progress >= 1;

  const iconConfig = REMINDER_ICONS[reminder.icon] || REMINDER_ICONS.timer;
  const IconComponent = iconConfig.icon;
  const iconColor = iconConfig.color;

  // Progress bar color changes as it approaches due
  const getProgressColor = () => {
    if (progress >= 1) return '#ef4444'; // red - due
    if (progress >= 0.8) return '#f59e0b'; // orange - almost due
    return iconColor; // normal color
  };

  return (
    <Card
      variant="outlined"
      sx={{
        p: 1.5,
        position: 'relative',
        border: isDue ? '2px solid' : '1px solid',
        borderColor: isDue ? 'error.main' : 'divider',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: isDue ? 'error.dark' : 'primary.main',
          bgcolor: 'action.hover',
        },
      }}
      onClick={() => onEdit(reminder)}
    >
      <Stack spacing={1}>
        {/* Header row */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              bgcolor: `${iconColor}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconComponent sx={{ fontSize: 16, color: iconColor }} />
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.75rem',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {reminder.label}
          </Typography>
        </Stack>

        {/* Progress bar */}
        <Box sx={{ position: 'relative', height: 8, bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: `${progress * 100}%`,
              bgcolor: getProgressColor(),
              borderRadius: 1,
              transition: 'width 0.3s, background-color 0.3s',
            }}
          />
        </Box>

        {/* Time remaining and refresh */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.65rem',
              color: isDue ? 'error.main' : 'text.secondary',
              fontWeight: isDue ? 600 : 400,
            }}
          >
            {timeRemaining}
          </Typography>
          <Tooltip title="Reset timer">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onReset(reminder.id);
              }}
              sx={{
                width: 24,
                height: 24,
                bgcolor: isDue ? 'error.main' : 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: isDue ? 'error.dark' : 'primary.dark' },
              }}
            >
              <RefreshIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
}

// Main Grow Reminders component
export default function GrowReminders({ growId }) {
  const { getReminders, addReminder, updateReminder, resetReminder, deleteReminder } = useGrow();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingReminder, setEditingReminder] = React.useState(null);
  const [, setTick] = React.useState(0);

  const reminders = getReminders(growId);

  // Force re-render every minute to update time remaining
  React.useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const handleOpenAdd = () => {
    setEditingReminder(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (reminder) => {
    setEditingReminder(reminder);
    setDialogOpen(true);
  };

  const handleSave = (reminderData) => {
    if (editingReminder) {
      updateReminder(growId, editingReminder.id, reminderData);
    } else {
      addReminder(growId, reminderData);
    }
  };

  const handleResetReminder = (reminderId) => {
    resetReminder(growId, reminderId);
  };

  const handleDeleteReminder = (reminderId) => {
    deleteReminder(growId, reminderId);
  };

  if (reminders.length === 0) {
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
            onClick={handleOpenAdd}
            sx={{
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              fontFamily: 'Inter, sans-serif',
              textTransform: 'none',
            }}
          >
            Add Reminder
          </Button>
        </Box>
        <ReminderDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSave={handleSave}
          onDelete={handleDeleteReminder}
          editingReminder={editingReminder}
        />
      </>
    );
  }

  return (
    <>
      {/* Header with add button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            color: 'text.secondary',
          }}
        >
          Reminders
        </Typography>
        <Tooltip title="Add reminder">
          <IconButton
            size="small"
            onClick={handleOpenAdd}
            sx={{
              width: 28,
              height: 28,
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Reminder grid - 4 per row */}
      <Grid container spacing={1.5}>
        {reminders.map((reminder) => (
          <Grid item xs={6} sm={3} key={reminder.id}>
            <ReminderMeter
              reminder={reminder}
              onReset={handleResetReminder}
              onEdit={handleOpenEdit}
            />
          </Grid>
        ))}
      </Grid>

      <ReminderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        onDelete={handleDeleteReminder}
        editingReminder={editingReminder}
      />
    </>
  );
}
